import { Component, OnInit } from '@angular/core';
import { ApiService } from '../../shared/services/api.service';
import { NgxSpinnerComponent, NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, forkJoin, of } from 'rxjs';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AccountData } from '../../models/account-data.model';
import { ErrorDirective } from '../../shared/directives/error.directive';
import { NotificationSettings } from '../../models/notification-settings.model';
import { HelpersService } from '../../shared/services/helpers.service';
import { ActivatedRoute, Router } from '@angular/router';

type TabKey = 'agency-info' | 'branding' | 'notifications' | 'security' | 'rules';

@Component({
  selector: 'app-settings',
  imports: [NgxSpinnerComponent, NgIf, ReactiveFormsModule, ErrorDirective, NgClass, NgForOf],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
})
export class SettingsComponent implements OnInit {
  loaded: boolean = false;
  accountForm!: FormGroup;
  notificationForm!: FormGroup;
  notificationSettings: NotificationSettings[] = [];
  tabs: { key: TabKey; label: string; icon: string }[] = [
    { key: 'agency-info', label: 'Agency Information', icon: 'ri-building-line' },
    { key: 'branding', label: 'Branding', icon: 'ri-palette-line' },
    // { key: 'notifications', label: 'Notifications', icon: 'ri-notification-line' },
    // { key: 'rules', label: 'Rules', icon: 'ri-list-check' },
  ];
  activeTab: TabKey = 'agency-info';
  imageURL: string | null = null;
  imageError: string = '';

  constructor(
    private apiService: ApiService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private fb: FormBuilder,
    private helpersService: HelpersService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit() {
    const tab = this.route.snapshot.queryParamMap.get('tab');
    if (tab) {
      this.activeTab = tab === 'agency-info' || tab === 'branding' ? tab : 'agency-info';
    }
    this.buildAccountForm();
    this.buildNotificationsForm();
    this.loadSettingsData();
  }

  setTab(tab: TabKey): void {
    this.activeTab = tab;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  buildAccountForm(): void {
    this.accountForm = this.fb.group({
      operational_email: ['', [Validators.email]],
      email_contact: ['', [Validators.required, Validators.email]],
      company_name: ['', Validators.required],
      address: ['', Validators.required],
      postal_code: ['', Validators.required],
      phone_contact: ['', Validators.required],
      city_name: [''],
      state_prov: [''],
      brand_color: [''],
    });
  }

  buildNotificationsForm(): void {
    this.notificationForm = this.fb.group({
      send_order_booking_emails: [false],
      send_order_payment_emails: [false],
    });
  }

  onLogoSelected(event: Event): void {
    this.imageError = '';
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];

    this.apiService.uploadLogo(file).subscribe({
      next: res => {
        if ('meta' in res) {
          this.imageURL = res.meta;
          this.toastr.success(res.message, 'Success');
        } else {
          this.imageError = res['error'] || 'Failed to upload logo.';
        }
      },
      error: err => {
        if (err?.error?.error) {
          this.imageError = err.error.error;
        } else {
          console.error('Logo upload error:', err);
        }
      },
    });
  }

  removeLogo() {
    this.apiService.removeLogo().subscribe({
      next: res => {
        if ('message' in res) {
          this.imageURL = '';
          this.imageError = '';
          this.toastr.success(res.message, 'Success');
        } else {
          this.imageError = res['error'] || 'Failed to remove logo.';
        }
      },
      error: err => {
        if (err?.error?.error) {
          this.imageError = err.error.error;
        } else {
          console.error('Logo removal error:', err);
        }
      },
    });
  }

  loadSettingsData(): void {
    this.loaded = false;
    this.spinner.show();

    forkJoin({
      account: this.apiService.getAccountData().pipe(
        catchError(error => {
          console.error('Account data error:', error);
          return of(null);
        })
      ),
      notificationSettings: this.apiService.getNotificationSettings().pipe(
        catchError(error => {
          console.error('Notification settings error:', error);
          return of(null);
        })
      ),
    })
      .pipe(
        finalize(() => {
          this.loaded = true;
          this.spinner.hide();
        })
      )
      .subscribe(({ account, notificationSettings }) => {
        if (account) {
          const data = account.data;
          this.accountForm.patchValue({
            operational_email: data.operational_email,
            email_contact: data.email_contact,
            company_name: data.company_name,
            address: data.address,
            postal_code: data.postal_code,
            phone_contact: data.phone_contact,
            city_name: data.city_name,
            state_prov: data.state_prov,
            brand_color: data.brand_color || '#A70000',
          });

          this.imageURL = data.agency_image_url;
        }

        if (notificationSettings) {
          this.notificationSettings = notificationSettings.data;
        }
      });
  }

  saveChanges() {
    const updatedData: AccountData = this.accountForm.getRawValue();
    this.apiService.updateAccountData(updatedData).subscribe({
      next: res => {
        if (res.status === 'OK') {
          this.toastr.success(res.message || 'Your changes have been saved.', 'Success');
        } else {
          this.helpersService.handleValidationErrors(
            this.accountForm,
            res.error as { [key: string]: string }
          );
          this.toastr.error('Please correct the highlighted fields.', 'Validation Error');
        }
      },
      error: err => {
        console.error('Error saving account data:', err);
      },
    });
  }

  toggleNotificationSettings(notification: NotificationSettings): void {
    const previousValue = notification.enabled;
    notification.enabled = !notification.enabled;
    this.toastr.clear();

    this.apiService.toggleNotificationSettings({ rule_id: notification.rule_id }).subscribe({
      next: res => {
        this.toastr.success(
          `Notifications ${res.data.enabled ? 'enabled' : 'disabled'} successfully`,
          'Success'
        );
        notification.enabled = res.data.enabled;
      },
      error: err => {
        console.error('Error toggling notification settings:', err);
        notification.enabled = previousValue;
      },
    });
  }
}
